import React, { useState, useRef, useEffect } from 'react';

const VocalRangeSlider = ({ 
  minValue = 'C2', 
  maxValue = 'G6', 
  onChange, 
  disabled = false,
  className = '' 
}) => {
  // Define the vocal range notes from C2 to G6
  const vocalNotes = [
    'C2', 'C#2', 'D2', 'D#2', 'E2', 'F2', 'F#2', 'G2', 'G#2', 'A2', 'A#2', 'B2',
    'C3', 'C#3', 'D3', 'D#3', 'E3', 'F3', 'F#3', 'G3', 'G#3', 'A3', 'A#3', 'B3',
    'C4', 'C#4', 'D4', 'D#4', 'E4', 'F4', 'F#4', 'G4', 'G#4', 'A4', 'A#4', 'B4',
    'C5', 'C#5', 'D5', 'D#5', 'E5', 'F5', 'F#5', 'G5', 'G#5', 'A5', 'A#5', 'B5',
    'C6', 'C#6', 'D6', 'D#6', 'E6', 'F6', 'F#6', 'G6'
  ];

  const [minIndex, setMinIndex] = useState(vocalNotes?.indexOf(minValue) || 0);
  const [maxIndex, setMaxIndex] = useState(vocalNotes?.indexOf(maxValue) || vocalNotes?.length - 1);
  const [isDragging, setIsDragging] = useState(null); // 'min', 'max', or null
  
  const sliderRef = useRef(null);
  const minThumbRef = useRef(null);
  const maxThumbRef = useRef(null);

  // Update indices when props change
  useEffect(() => {
    const newMinIndex = vocalNotes?.indexOf(minValue);
    const newMaxIndex = vocalNotes?.indexOf(maxValue);
    
    if (newMinIndex !== -1) setMinIndex(newMinIndex);
    if (newMaxIndex !== -1) setMaxIndex(newMaxIndex);
  }, [minValue, maxValue]);

  // Notify parent of changes
  useEffect(() => {
    onChange?.(vocalNotes?.[minIndex], vocalNotes?.[maxIndex]);
  }, [minIndex, maxIndex, onChange]);

  const getPercentage = (index) => {
    return (index / (vocalNotes?.length - 1)) * 100;
  };

  const handleMouseDown = (type) => (e) => {
    if (disabled) return;
    e?.preventDefault();
    setIsDragging(type);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || disabled || !sliderRef?.current) return;
    
    e?.preventDefault();
    const rect = sliderRef?.current?.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(100, ((e?.clientX - rect?.left) / rect?.width) * 100));
    const newIndex = Math.round((percentage / 100) * (vocalNotes?.length - 1));

    if (isDragging === 'min') {
      const constrainedIndex = Math.min(newIndex, maxIndex);
      setMinIndex(constrainedIndex);
    } else if (isDragging === 'max') {
      const constrainedIndex = Math.max(newIndex, minIndex);
      setMaxIndex(constrainedIndex);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(null);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, minIndex, maxIndex]);

  const handleTrackClick = (e) => {
    if (disabled || isDragging) return;
    
    const rect = sliderRef?.current?.getBoundingClientRect();
    const percentage = ((e?.clientX - rect?.left) / rect?.width) * 100;
    const clickIndex = Math.round((percentage / 100) * (vocalNotes?.length - 1));
    
    // Determine which thumb is closer and move that one
    const minDistance = Math.abs(clickIndex - minIndex);
    const maxDistance = Math.abs(clickIndex - maxIndex);
    
    if (minDistance < maxDistance) {
      const constrainedIndex = Math.min(clickIndex, maxIndex);
      setMinIndex(constrainedIndex);
    } else {
      const constrainedIndex = Math.max(clickIndex, minIndex);
      setMaxIndex(constrainedIndex);
    }
  };

  return (
    <div className={`vocal-range-slider ${className}`}>
      {/* Range Display */}
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm font-medium text-blue-100">
          Min: {vocalNotes?.[minIndex]}
        </span>
        <span className="text-sm font-medium text-blue-100">
          Max: {vocalNotes?.[maxIndex]}
        </span>
      </div>
      {/* Slider Track */}
      <div 
        ref={sliderRef}
        className="relative h-6 bg-white bg-opacity-20 rounded-lg cursor-pointer"
        onClick={handleTrackClick}
      >
        {/* Active Range */}
        <div
          className="absolute h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-lg"
          style={{
            left: `${getPercentage(minIndex)}%`,
            width: `${getPercentage(maxIndex) - getPercentage(minIndex)}%`
          }}
        />
        
        {/* Min Thumb */}
        <div
          ref={minThumbRef}
          className={`absolute w-6 h-6 bg-white rounded-full shadow-lg cursor-grab transform -translate-y-0 -translate-x-1/2 border-2 border-purple-500 ${isDragging === 'min' ? 'cursor-grabbing scale-110' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ left: `${getPercentage(minIndex)}%` }}
          onMouseDown={handleMouseDown('min')}
        />
        
        {/* Max Thumb */}
        <div
          ref={maxThumbRef}
          className={`absolute w-6 h-6 bg-white rounded-full shadow-lg cursor-grab transform -translate-y-0 -translate-x-1/2 border-2 border-indigo-500 ${isDragging === 'max' ? 'cursor-grabbing scale-110' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ left: `${getPercentage(maxIndex)}%` }}
          onMouseDown={handleMouseDown('max')}
        />
      </div>
      {/* Note Labels */}
      <div className="flex justify-between mt-2 text-xs text-blue-200">
        <span>C2</span>
        <span>C3</span>
        <span>C4</span>
        <span>C5</span>
        <span>C6</span>
        <span>G6</span>
      </div>
    </div>
  );
};

export default VocalRangeSlider;